import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';

const TwoFAVerify: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error } = useAuth();
  
  // État pour les 6 chiffres du code OTP
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(6).fill(''));
  
  // Récupérer les identifiants de connexion et le returnUrl depuis l'état de navigation
  const credentials = React.useMemo(() => {
    return location.state?.credentials || { email: '', password: '' };
  }, [location.state]);
  
  const returnUrl = React.useMemo(() => {
    return location.state?.returnUrl || null;
  }, [location.state]);
  
  // Référence pour les inputs
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);
  
  // Effet pour focus sur le premier input au chargement
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
    
    // Si pas d'identifiants, rediriger vers la page de connexion
    if (!credentials.email || !credentials.password) {
      navigate('/login');
    }
  }, [credentials, navigate]);
  
  // Gérer le changement de valeur d'un chiffre
  const handleDigitChange = (index: number, value: string) => {
    // Ne garder que les chiffres
    const digit = value.replace(/[^0-9]/g, '');
    
    // Mettre à jour l'état
    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = digit;
    setOtpDigits(newOtpDigits);
    
    // Si un chiffre est entré et ce n'est pas le dernier input, passer au suivant
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  
  // Gérer le collage d'un code complet
  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    // Empêcher le comportement par défaut
    e.preventDefault();
    
    // Récupérer le texte collé
    const pastedData = e.clipboardData.getData('text');
    
    // Ne garder que les chiffres
    const digits = pastedData.replace(/[^0-9]/g, '').slice(0, 6);
    
    // Si le code collé est valide
    if (digits.length > 0) {
      // Créer un nouveau tableau de chiffres
      const newOtpDigits = [...otpDigits];
      
      // Remplir le tableau avec les chiffres collés
      for (let i = 0; i < Math.min(digits.length, 6); i++) {
        newOtpDigits[i] = digits[i];
      }
      
      // Mettre à jour l'état
      setOtpDigits(newOtpDigits);
      
      // Focus sur le dernier champ rempli ou le suivant
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };
  
  // Gérer les touches spéciales (backspace, etc.)
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Si backspace sur un input vide, revenir au précédent
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  
  // Gérer la soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Combiner les chiffres en un seul code
    const otpCode = otpDigits.join('');
    
    // Vérifier que le code est complet
    if (otpCode.length !== 6) {
      return;
    }
    
    console.log('Submitting OTP code:', otpCode);
    
    // Appeler l'API de connexion avec le code OTP
    const result = await login({
      email: credentials.email,
      password: credentials.password,
      otpCode
    });
    
    console.log('Login result:', result);
    
    // Si la connexion réussit, rediriger vers returnUrl s'il existe, sinon vers le tableau de bord
    if (result.success) {
      if (returnUrl) {
        navigate(returnUrl);
      } else {
        navigate('/dashboard');
      }
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Vérification à deux facteurs
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Veuillez entrer le code à 6 chiffres généré par votre application d'authentification
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Code d'authentification
              </label>
              <div className="flex justify-between gap-2">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                      return undefined;
                    }}
                    type="text"
                    maxLength={1}
                    className="w-12 h-12 text-center text-xl font-bold border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    value={otpDigits[index]}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={(e) => handlePaste(index, e)}
                    autoComplete="off"
                  />
                ))}
              </div>
            </div>

            <div>
              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={isLoading}
                disabled={otpDigits.join('').length !== 6}
              >
                Vérifier
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default TwoFAVerify;
